import { RUNTIME_BACKEND_URL } from '../../lib/backendUrl';
import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Calendar } from '../../components/ui/calendar';
import { toast } from 'sonner';
import {
  ArrowLeft,
  ArrowRight,
  Users,
  CalendarDays,
  Building,
  Loader2,
  Music,
  Camera,
  Video,
  Wine,
  Cookie,
  UtensilsCrossed,
  Check,
  Clock,
  AlertCircle,
  FileText
} from 'lucide-react';

const API = RUNTIME_BACKEND_URL;

export default function SalonNewCustomerPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token } = useAuthStore();
  const [saving, setSaving] = useState(false);
  const [venues, setVenues] = useState([]);
  const [bookedDates, setBookedDates] = useState([]);
  const [reservationsOnDate, setReservationsOnDate] = useState([]);
  const [currentStep, setCurrentStep] = useState(1); // 1: Salon, 2: Takvim, 3: Müşteri, 4: Paket
  
  const [formData, setFormData] = useState({
    // Salon seçimi
    venue_id: '',
    venue_name: '',
    // Tarih bilgileri
    event_date: '',
    event_time_start: '18:00',
    event_time_end: '23:00',
    // Müşteri bilgileri
    bride_name: '',
    groom_name: '',
    phone1: '',
    phone2: '',
    email: '',
    guest_count: '',
    // Paket içerikleri
    has_orchestra: false,
    has_camera: false,
    has_photography: false,
    has_drinks: false,
    has_snacks: false,
    has_food: false,
    custom_services: '',
    // Ücret
    total_price: 0,
    deposit_amount: 0,
    notes: ''
  });

  useEffect(() => {
    loadVenues();
    loadAllReservations();
  }, []);

  const loadVenues = async () => {
    try {
      const res = await fetch(`${API}/api/salon/venues`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setVenues(data);
      }
    } catch (error) {
      console.error('Venues load error:', error);
    }
  };

  const loadAllReservations = async () => {
    try {
      const res = await fetch(`${API}/api/salon/customers`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const customers = await res.json();
        // Tüm rezervasyonlu tarihleri al
        const dates = customers
          .map(c => c.event_date)
          .filter(d => d)
          .map(d => new Date(d));
        setBookedDates(dates);
      }
    } catch (error) {
      console.error('Reservations load error:', error);
    }
  };

  const loadReservationsForDate = async (date) => {
    try {
      // UTC yerine local date kullan (timezone fix)
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const formattedDate = `${year}-${month}-${day}`;
      
      const res = await fetch(`${API}/api/salon/customers/by-date/${formattedDate}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setReservationsOnDate(data);
      }
    } catch (error) {
      console.error('Reservations for date load error:', error);
    }
  };

  // Tarihin dolu olup olmadığını kontrol et
  const isDateBooked = (date) => {
    return bookedDates.some(bookedDate => 
      bookedDate.toDateString() === date.toDateString()
    );
  };

  const handleVenueSelect = (venue) => {
    setFormData({ ...formData, venue_id: venue.id, venue_name: venue.name });
  };

  const handleDateSelect = (date) => {
    if (date) {
      // UTC yerine local date kullan (timezone fix)
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const formattedDate = `${year}-${month}-${day}`;
      
      setFormData({ ...formData, event_date: formattedDate });
      // O tarihteki rezervasyonları yükle
      loadReservationsForDate(date);
    }
  };

  const nextStep = () => {
    if (currentStep === 1) {
      if (!formData.venue_id) {
        toast.error('Lütfen bir salon seçin');
        return;
      }
    }
    if (currentStep === 2) {
      if (!formData.event_date) {
        toast.error('Lütfen tarih seçin');
        return;
      }
    }
    if (currentStep === 3) {
      if (!formData.bride_name || !formData.groom_name || !formData.phone1) {
        toast.error('Lütfen zorunlu alanları doldurun');
        return;
      }
    }
    setCurrentStep(currentStep + 1);
  };

  const prevStep = () => {
    setCurrentStep(currentStep - 1);
  };

  const handleSubmit = async () => {
    if (!formData.total_price) {
      toast.error('Lütfen toplam anlaşma tutarını girin');
      return;
    }

    setSaving(true);
    try {
      const res = await fetch(`${API}/api/salon/customers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...formData,
          guest_count: formData.guest_count ? parseInt(formData.guest_count) : null
        })
      });

      if (!res.ok) throw new Error('Kaydedilemedi');

      const data = await res.json();
      toast.success('Rezervasyon oluşturuldu');
      navigate(`/salon/musteriler/${data.id}`);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('tr-TR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      weekday: 'long'
    });
  };

  const stepLabels = ['Salon Seçimi', 'Tarih Seçimi', 'Müşteri Bilgileri', 'Paket & Ücret'];

  return (
    <div className="space-y-6" data-testid="salon-new-customer">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/salon/musteriler')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <h1 className="text-2xl font-bold">Yeni Rezervasyon</h1>
      </div>

      {/* Progress Steps */}
      <div className="flex items-center justify-center gap-2">
        {[1, 2, 3, 4].map((step) => (
          <div key={step} className="flex items-center">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center font-medium transition-colors ${
              currentStep >= step 
                ? 'bg-primary text-primary-foreground' 
                : 'bg-muted text-muted-foreground'
            }`}>
              {currentStep > step ? <Check className="h-5 w-5" /> : step}
            </div>
            {step < 4 && (
              <div className={`w-12 sm:w-16 h-1 mx-2 ${
                currentStep > step ? 'bg-primary' : 'bg-muted'
              }`} />
            )}
          </div>
        ))}
      </div>
      <div className="flex justify-center gap-4 sm:gap-8 text-xs sm:text-sm text-muted-foreground">
        {stepLabels.map((label, idx) => (
          <span key={idx} className={currentStep === idx + 1 ? 'text-primary font-medium' : ''}>{label}</span>
        ))}
      </div>

      {/* Step 1: Salon Seçimi */}
      {currentStep === 1 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building className="h-5 w-5 text-primary" />
              Salon Seçin
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {venues.length === 0 ? (
              <div className="text-center py-8">
                <Building className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                <p className="text-muted-foreground mb-4">Henüz salon eklenmemiş.</p>
                <Button variant="outline" onClick={() => navigate('/salon/ayarlar')}>
                  Salon Ekle
                </Button>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {venues.map((venue) => (
                  <div
                    key={venue.id}
                    onClick={() => handleVenueSelect(venue)}
                    className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      formData.venue_id === venue.id
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:border-primary/50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Building className="h-6 w-6 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium text-lg">{venue.name}</p>
                        <p className="text-sm text-muted-foreground flex items-center gap-1">
                          <Users className="h-3 w-3" />
                          {venue.capacity} Kişilik
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="flex justify-end pt-4">
              <Button onClick={nextStep} disabled={!formData.venue_id}>
                İleri
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 2: Tarih Seçimi */}
      {currentStep === 2 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CalendarDays className="h-5 w-5 text-primary" />
              Düğün Tarihi Seçin
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              Seçilen Salon: <strong>{formData.venue_name}</strong>
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-center">
              <Calendar
                mode="single"
                selected={formData.event_date ? new Date(formData.event_date) : undefined}
                onSelect={handleDateSelect}
                disabled={(date) => date < new Date()}
                modifiers={{
                  booked: bookedDates
                }}
                modifiersClassNames={{
                  booked: "bg-amber-100 text-amber-800 font-medium"
                }}
                className="rounded-md border"
              />
            </div>
            
            {bookedDates.length > 0 && (
              <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                <div className="w-4 h-4 bg-amber-100 rounded"></div>
                <span>Bu tarihlerde rezervasyon var (seçilebilir)</span>
              </div>
            )}

            {formData.event_date && (
              <div className="text-center p-4 bg-primary/10 rounded-lg">
                <p className="text-lg font-medium">{formatDate(formData.event_date)}</p>
              </div>
            )}

            {/* O tarihteki mevcut rezervasyonlar */}
            {formData.event_date && reservationsOnDate.length > 0 && (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-center gap-2 text-amber-800 mb-2">
                  <AlertCircle className="h-4 w-4" />
                  <span className="font-medium">Bu tarihte mevcut rezervasyonlar:</span>
                </div>
                <div className="space-y-2">
                  {reservationsOnDate.map((res) => (
                    <div key={res.id} className="flex items-center gap-2 text-sm text-amber-700 bg-white p-2 rounded">
                      <Building className="h-4 w-4" />
                      <span className="font-medium">{res.venue_name || 'Salon'}</span>
                      <span>-</span>
                      <Clock className="h-4 w-4" />
                      <span>{res.event_time_start} - {res.event_time_end}</span>
                      <span>-</span>
                      <span>{res.bride_name} & {res.groom_name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4 max-w-md mx-auto">
              <div className="space-y-2">
                <Label>Başlangıç Saati</Label>
                <Input
                  type="time"
                  value={formData.event_time_start}
                  onChange={(e) => setFormData({...formData, event_time_start: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Bitiş Saati</Label>
                <Input
                  type="time"
                  value={formData.event_time_end}
                  onChange={(e) => setFormData({...formData, event_time_end: e.target.value})}
                />
              </div>
            </div>

            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={prevStep}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Geri
              </Button>
              <Button onClick={nextStep} disabled={!formData.event_date}>
                İleri
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 3: Müşteri Bilgileri */}
      {currentStep === 3 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-primary" />
              Müşteri Bilgileri
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              {formData.venue_name} • {formatDate(formData.event_date)} • {formData.event_time_start} - {formData.event_time_end}
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Gelin Adı *</Label>
                <Input
                  value={formData.bride_name}
                  onChange={(e) => setFormData({...formData, bride_name: e.target.value})}
                  placeholder="Gelin adı"
                />
              </div>
              <div className="space-y-2">
                <Label>Damat Adı *</Label>
                <Input
                  value={formData.groom_name}
                  onChange={(e) => setFormData({...formData, groom_name: e.target.value})}
                  placeholder="Damat adı"
                />
              </div>
              <div className="space-y-2">
                <Label>Cep 1 *</Label>
                <Input
                  value={formData.phone1}
                  onChange={(e) => setFormData({...formData, phone1: e.target.value})}
                  placeholder="05XX XXX XX XX"
                />
              </div>
              <div className="space-y-2">
                <Label>Cep 2</Label>
                <Input
                  value={formData.phone2}
                  onChange={(e) => setFormData({...formData, phone2: e.target.value})}
                  placeholder="05XX XXX XX XX"
                />
              </div>
              <div className="space-y-2">
                <Label>E-posta</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  placeholder="ornek@email.com"
                />
              </div>
              <div className="space-y-2">
                <Label>Davetli Sayısı</Label>
                <Input
                  type="number"
                  value={formData.guest_count}
                  onChange={(e) => setFormData({...formData, guest_count: e.target.value})}
                  placeholder="Tahmini davetli sayısı"
                />
              </div>
            </div>

            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={prevStep}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Geri
              </Button>
              <Button onClick={nextStep}>
                İleri
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 4: Paket & Ücret */}
      {currentStep === 4 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-primary" />
              Paket İçerikleri & Ücret
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              {formData.bride_name} & {formData.groom_name} • {formData.venue_name} • {formatDate(formData.event_date)}
            </p>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Paket İçerikleri */}
            <div>
              <Label className="text-base font-medium mb-3 block">Dahil Hizmetler</Label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center gap-2">
                    <Music className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">Orkestra</span>
                  </div>
                  <Switch
                    checked={formData.has_orchestra}
                    onCheckedChange={(checked) => setFormData({...formData, has_orchestra: checked})}
                  />
                </div>
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center gap-2">
                    <Video className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">Kamera</span>
                  </div>
                  <Switch
                    checked={formData.has_camera}
                    onCheckedChange={(checked) => setFormData({...formData, has_camera: checked})}
                  />
                </div>
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center gap-2">
                    <Camera className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">Fotoğraf</span>
                  </div>
                  <Switch
                    checked={formData.has_photography}
                    onCheckedChange={(checked) => setFormData({...formData, has_photography: checked})}
                  />
                </div>
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center gap-2">
                    <Wine className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">İçecek</span>
                  </div>
                  <Switch
                    checked={formData.has_drinks}
                    onCheckedChange={(checked) => setFormData({...formData, has_drinks: checked})}
                  />
                </div>
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center gap-2">
                    <Cookie className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">Çerez</span>
                  </div>
                  <Switch
                    checked={formData.has_snacks}
                    onCheckedChange={(checked) => setFormData({...formData, has_snacks: checked})}
                  />
                </div>
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center gap-2">
                    <UtensilsCrossed className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">Yemek</span>
                  </div>
                  <Switch
                    checked={formData.has_food}
                    onCheckedChange={(checked) => setFormData({...formData, has_food: checked})}
                  />
                </div>
              </div>
            </div>

            {/* Özel İçerik */}
            <div className="space-y-2">
              <Label>Özel İçerik / Ek Hizmetler</Label>
              <Textarea
                value={formData.custom_services}
                onChange={(e) => setFormData({...formData, custom_services: e.target.value})}
                placeholder="Örn: DJ, Işık Sistemi, Havai Fişek..."
                rows={2}
              />
            </div>

            {/* Ücret Bilgileri */}
            <div className="pt-4 border-t">
              <Label className="text-base font-medium mb-3 block">Ücret Bilgileri</Label>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Toplam Anlaşma Tutarı (TL) *</Label>
                  <Input
                    type="number"
                    value={formData.total_price || ''}
                    onChange={(e) => setFormData({...formData, total_price: parseFloat(e.target.value) || 0})}
                    onFocus={(e) => e.target.select()}
                    placeholder="Tutar girin"
                    className="text-lg"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Kapora (TL)</Label>
                  <Input
                    type="number"
                    value={formData.deposit_amount || ''}
                    onChange={(e) => setFormData({...formData, deposit_amount: parseFloat(e.target.value) || 0})}
                    onFocus={(e) => e.target.select()}
                    placeholder="Kapora girin"
                    className="text-lg"
                  />
                </div>
              </div>
              {formData.total_price > 0 && formData.deposit_amount > 0 && (
                <p className="text-sm text-muted-foreground mt-2">
                  Kalan: {formatCurrency(formData.total_price - formData.deposit_amount)}
                </p>
              )}
            </div>

            {/* Notlar */}
            <div className="space-y-2">
              <Label>Notlar</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                placeholder="Ek notlar..."
                rows={2}
              />
            </div>

            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={prevStep}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Geri
              </Button>
              <Button onClick={handleSubmit} disabled={saving}>
                {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Kaydet
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
